import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { LocaleService, initializeLocale } from './utils/locale.service';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LocaleDatePipe } from './utils/wizer.pipes';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ResetPasswordResolver } from './resolvers/wizer.resolver';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { FormsModule } from '@angular/forms';
import { SharedModule } from './shared/shared.module';
import { UtilsModule } from './utils/utils.module';
import { WizerHttpInterceptor } from './shared/common/wizer-http-interceptor.service';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import en from '@angular/common/locales/en';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from './layout/layout.module';
import { RouterExtService } from './shared/services/route.service';
import {
  MAT_DATE_LOCALE,
  DateAdapter,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';
import { ToastrModule } from 'ngx-toastr';
import { I18nModule } from './i18n/i18n.module';
import { IndexedDBService } from './indexDB/indexedDB.service';
import { LoginModule } from './auth/login/login.module';
import { CorporateClientCompareComponent } from './pages/client-management/corporate/corporate-client-compare/corporate-client-compare.component';
import { VendorDealerDialogModule } from './pages/settings/configurations/loan-products/new-loan-product/vendor-dealer-dialog/vendor-dealer-dialog.module';

registerLocaleData(localeFr);
registerLocaleData(localeEs);
registerLocaleData(localeNl);
registerLocaleData(localePt);
registerLocaleData(localeEn);
registerLocaleData(en);


/** Staging/grp set `serviceWorker: false` — unregister any prior SW so ngsw cannot intercept API calls. */
export function unregisterStaleServiceWorker(): () => Promise<void> {
  return () => {
    const env = environment as { serviceWorker?: boolean };
    if (typeof navigator === 'undefined' || env.serviceWorker !== false) {
      return Promise.resolve();
    }
    return navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
      .then(() => undefined);
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    SharedModule,
    VendorDealerDialogModule,
    AppRoutingModule,
    UtilsModule,
    FormsModule,
    HttpClientModule,
    LayoutModule,
    NgIdleKeepaliveModule.forRoot(),
    // preventDuplicates: an identical message fired more than once (e.g. a guarded
    // async check that still ends up triggered twice) stacks as repeated toasts
    // instead of being collapsed into the one already on screen.
    ToastrModule.forRoot({
      preventDuplicates: true,
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled:
        environment.production &&
        (environment as { serviceWorker?: boolean }).serviceWorker !== false,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    BrowserAnimationsModule,
  ],
  providers: [
    I18nModule.setLocale(),
    I18nModule.setLocaleId(),
    LocaleService,
    {
      provide: APP_INITIALIZER,
      useFactory: unregisterStaleServiceWorker,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLocale,
      deps: [LocaleService],
      multi: true,
    },
    ResetPasswordResolver,
    DatePipe,
    DecimalPipe,
    RouterExtService,

    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeService: LocaleService) => localeService.getLocale(),
      deps: [LocaleService],
    },

    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS,
    },

    {
      provide: NZ_I18N,
      useFactory: (localeService: LocaleService) => localeService.getNzLocale(),
      deps: [LocaleService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WizerHttpInterceptor,
      multi: true,
    },
    IndexedDBService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
